import React, { useEffect, useMemo, useRef, useState } from 'react';
import EmptyState from '../components/EmptyState';
import {
  RELEASE_NOTES,
  RELEASE_NOTE_TYPE_ORDER,
  RELEASE_NOTE_TYPE_LABELS,
  RELEASE_NOTES_PER_PAGE
} from '../config/releaseNotes';
import './ReleaseNotesPage.css';

/**
 * Release Notes Page (Feature 37)
 *
 * Renders the human-written release notes from `src/config/releaseNotes.js`,
 * newest-first. Each release groups its items under Dutch type headings
 * (Nieuwe functies / Verbeteringen / Oplossingen). The entry that matches the
 * running app version is flagged as "Huidige versie".
 *
 * The list is paginated (fixed page size, Prev/Next + page numbers) so the
 * oldest history stays reachable without an endlessly long scroll.
 */

const MONTHS_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'
];

/**
 * Formats an ISO `YYYY-MM-DD` date as a Dutch, human-readable string.
 * Uses the local date parts (like the rest of the app) to avoid a UTC off-by-one.
 *
 * @param {string} isoDate
 * @returns {string}
 */
const formatReleaseDate = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || ''));
  if (!match) return String(isoDate || '');
  const [, year, month, day] = match;
  const monthName = MONTHS_NL[Number(month) - 1];
  return `${Number(day)} ${monthName} ${year}`;
};

const ReleaseNotesPage = () => {
  const appVersion = process.env.REACT_APP_VERSION || '';
  const [page, setPage] = useState(1);
  const listTopRef = useRef(null);

  const pageCount = Math.max(1, Math.ceil(RELEASE_NOTES.length / RELEASE_NOTES_PER_PAGE));
  const currentPage = Math.min(page, pageCount);

  const visibleNotes = useMemo(() => {
    const start = (currentPage - 1) * RELEASE_NOTES_PER_PAGE;
    return RELEASE_NOTES.slice(start, start + RELEASE_NOTES_PER_PAGE);
  }, [currentPage]);

  // Jumping between pages would otherwise leave the reader mid-scroll, so bring
  // the top of the list back into view after a page change (smooth, but instant
  // when the user prefers reduced motion).
  useEffect(() => {
    const element = listTopRef.current;
    if (!element || typeof element.scrollIntoView !== 'function') return;
    const prefersReducedMotion = (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    element.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  }, [currentPage]);

  const goToPage = (nextPage) => {
    setPage(Math.min(Math.max(nextPage, 1), pageCount));
  };

  const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <div className="release-notes-page">
      <div className="app-page-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">Wat is nieuw</h2>
          </div>
          <p className="app-page-description">
            De laatste wijzigingen en verbeteringen in de app, per versie.
          </p>
        </div>
      </div>

      {RELEASE_NOTES.length === 0 ? (
        <EmptyState
          icon="📝"
          message="Er zijn nog geen release notes."
          hint="Wijzigingen worden hier per versie vastgelegd."
        />
      ) : (
        <>
          <ol className="release-notes-list" ref={listTopRef}>
            {visibleNotes.map((entry) => {
              const isCurrent = appVersion && entry.version === appVersion;

              return (
                <li
                  key={entry.version}
                  className={`release-card${isCurrent ? ' is-current' : ''}`}
                >
                  <div className="release-card-header">
                    <div className="release-card-heading">
                      <span className="release-version">Versie {entry.version}</span>
                      {isCurrent && (
                        <span className="release-current-badge">Huidige versie</span>
                      )}
                    </div>
                    <span className="release-date">{formatReleaseDate(entry.date)}</span>
                  </div>

                  {entry.title && <h3 className="release-title">{entry.title}</h3>}

                  <div className="release-groups">
                    {RELEASE_NOTE_TYPE_ORDER.map((type) => {
                      const items = entry.items.filter((item) => item.type === type);
                      if (items.length === 0) return null;

                      return (
                        <div key={type} className="release-group">
                          <h4 className="release-group-title">
                            {RELEASE_NOTE_TYPE_LABELS[type]}
                          </h4>
                          <ul className="release-items">
                            {items.map((item) => (
                              <li key={item.text} className="release-item">
                                {item.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ol>

          <nav className="release-pagination" aria-label="Paginering">
            <button
              type="button"
              className="release-page-btn release-page-nav"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              aria-label="Vorige pagina"
            >
              ‹ Vorige
            </button>

            <ul className="release-page-numbers">
              {pageNumbers.map((number) => (
                <li key={number}>
                  <button
                    type="button"
                    className={`release-page-btn release-page-number${number === currentPage ? ' is-active' : ''}`}
                    onClick={() => goToPage(number)}
                    aria-label={`Pagina ${number}`}
                    aria-current={number === currentPage ? 'page' : undefined}
                  >
                    {number}
                  </button>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="release-page-btn release-page-nav"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= pageCount}
              aria-label="Volgende pagina"
            >
              Volgende ›
            </button>
          </nav>
        </>
      )}
    </div>
  );
};

export default ReleaseNotesPage;
